import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Box,
  Drawer,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router';
import MenuIcon from '@mui/icons-material/Menu';
import FilterListIcon from '@mui/icons-material/FilterList';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { NavDrawerContent } from '../drawers/NavDrawerContent';
import { FilterDrawerContent } from '../drawers/FiltersDrawerContent';
import PdfReportModal from '../pdf-report-modal/PdfReportModal';
import { clearAppSelections } from '../../services/qlikSelectionService';
import { QLIK_APP_REF, LOGO_HREF, DRAWER_WIDTH_OPENED, DRAWER_WIDTH_CLOSED, FILTER_DRAWER_WIDTH, REGION_FIELD, CYBER_CONFIG } from '../../constants';
import { ExtraControlsConfig } from '../../types';

const drawerWidthOpened = DRAWER_WIDTH_OPENED;
const drawerWidthClosed = DRAWER_WIDTH_CLOSED;
const filterDrawerWidth = FILTER_DRAWER_WIDTH;

const logoHref = LOGO_HREF

type AppBarComponentProps = {
  extraControls?: ExtraControlsConfig;
  filterElements?: React.ReactNode;
  onOpenFilters?: () => void;
};

const Logo = () => (
  <Box component="a"
    href={logoHref}
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Chubb (opens in new tab)"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      textDecoration: 'none',
      color: 'inherit',
      cursor: 'pointer',
    }}
  >
    <Box
      component="img"
      src="/assets/images/logos/chubb-white.svg"
      alt="Chubb"
      sx={{ height: 24, width: '110px' }}
    />
  </Box>
);

const AppBarComponent: React.FC<AppBarComponentProps> = ({ 
  extraControls, 
  filterElements,
  onOpenFilters,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const isReduced = useMediaQuery(theme.breakpoints.up('md'));
  // const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  const [hoverOpen, setHoverOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [isPdfReportModalOpen, setIsPdfReportModalOpen] = useState(false);

  const appSelections = useSelector((state: RootState) => state.qlikSelection.qlikAppSelections[QLIK_APP_REF]);

  // const baseToolbarHeight = useMemo(() => {
  //   const minH = (theme.mixins.toolbar as any)?.minHeight;
  //   return typeof minH === 'number' ? minH : (isSmDown ? 50 : 64);
  // }, [theme, isSmDown]);
  const baseToolbarHeight = 64

  const extraBarHeight = 0; // was: extraControls ? 48 : 0;
  const headerTotalHeight = baseToolbarHeight + extraBarHeight;

  useEffect(() => {
    if(appSelections) {
      const regionSelections = appSelections?.filter((field: any) => field?.qField === REGION_FIELD);
      if (regionSelections && regionSelections.length > 0) {
        setLinkUrl(regionSelections[0]?.qSelected == 'USA' ? CYBER_CONFIG.appBar.gotoLink.usLink : CYBER_CONFIG.appBar.gotoLink.intlLink);
        setLinkText(regionSelections[0]?.qSelected == 'USA' ? CYBER_CONFIG.appBar.gotoLink.usText : CYBER_CONFIG.appBar.gotoLink.intlText);
      }
    }
  }, [appSelections])

  const handleNavToggle = () => {
    setNavOpen((p) => !p);
    // Close filter drawer if opening nav drawer
    if (!navOpen) {
      setFiltersOpen(false);
    }
  };
  const handleFiltersToggle = () => {
    setFiltersOpen((p) => !p);
    // Close nav drawer if opening filter drawer
    if (!filtersOpen) {
      setNavOpen(false);
    }
    onOpenFilters?.();
  };

  const handleCustomizeReportClick = () => {
    setIsPdfReportModalOpen(true);
  };

  const handlePdfReportClose = () => {
    setIsPdfReportModalOpen(false);
  };

  const isExpanded = hoverOpen || navOpen;

  return (
    <>
      <AppBar
        position="sticky"
        color="primary"
        elevation={4}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {/* Desktop / Large screens */}
        <Toolbar sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
          <Logo />
          <Box
            sx={{
              borderLeft: 2,
              borderColor: 'primary.contrastText',
              pl: 1,
            }}
          >                   
            <Typography variant="h6" color="primary.main.contrastText" sx={{ ml: 1 }}>
              Chubb Cyber Index
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              color="primary"
              sx={{ textTransform: 'none' }}
              onClick={handleCustomizeReportClick}
              startIcon={<PictureAsPdfIcon />}
            >
              Customize Report
            </Button>
            {linkUrl && (
              <Button
                variant="contained"
                color="primary"
                sx={{ textTransform: 'none' }}
                onClick={() => window.open(linkUrl, '_blank')}
                startIcon={<OpenInNewIcon />}
              >
                {linkText}
              </Button>
            )}
        </Toolbar>

        {/* Mobile / Small screens */}
        <Toolbar
          sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'relative',
            minHeight: baseToolbarHeight,
          }}
        >
          <IconButton
            edge="start"
            aria-label="menu"
            onClick={handleNavToggle}
            sx={{ position: 'absolute', left: 16, color: theme.palette.primary.contrastText }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Logo />
          </Box>

          {filterElements && (
            <Box sx={{ ml: 'auto' }}>
              <Button
                variant="text"
                startIcon={<FilterListIcon />}
                sx={{ textTransform: 'none', display: { xs: 'inline-flex', lg: 'none' }, color: theme.palette.primary.contrastText }}
                onClick={handleFiltersToggle}
              >
                Filters
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Left Navigation Drawer */}
      <Drawer
        variant={isReduced ? 'permanent' : 'temporary'}
        open={isReduced ? true : navOpen}
        onClose={!isReduced ? handleNavToggle : undefined}
        ModalProps={{ keepMounted: true }}
        onMouseEnter={isReduced ? () => setHoverOpen(true) : undefined}
        onMouseLeave={isReduced ? () => setHoverOpen(false) : undefined}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            top: `${headerTotalHeight}px`,
            height: `calc(100vh - ${headerTotalHeight}px)`,
            borderRight: 1,
            borderColor: 'divider',
            width: isReduced ? (hoverOpen ? drawerWidthOpened : drawerWidthClosed) : drawerWidthOpened,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
            backgroundColor: theme.custom.darkGrey,
            color: theme.palette.primary.contrastText,
            '& .MuiListItemText-primary': { color: theme.palette.primary.contrastText },
            '& .MuiListItemIcon-root, & .MuiSvgIcon-root': { color: theme.palette.primary.contrastText },
            '& .MuiListItemIcon-root': { minWidth: 40,},
            '& .MuiDivider-root': {
              borderColor: 'rgba(255, 255, 255, 0.15)',
              backgroundColor: 'rgba(255, 255, 255, 0.15)', // helps vertical/middle variants
            },
          },
        }}
      >
        <NavDrawerContent
          expanded={isExpanded}
          extraControls={extraControls}
          isMdDown={isMdDown}
          onCustomizeReport={handleCustomizeReportClick}
        />
      </Drawer>

      {/* Right Filters Drawer */}
      {filterElements && (
        <Drawer
          anchor="right"
          variant="temporary"
          open={isReduced ? false : filtersOpen}
          onClose={handleFiltersToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              top: `${headerTotalHeight}px`,
              height: `calc(100vh - ${headerTotalHeight}px)`,
              width: filterDrawerWidth,
              borderLeft: 1,
              borderColor: 'divider',
              // backgroundColor: theme.custom.darkGrey,
              color: theme.palette.primary.contrastText,
              '& .MuiListItemText-primary': { color: theme.palette.primary.contrastText },
              // '& .MuiListItemIcon-root, & .MuiSvgIcon-root': { color: theme.palette.primary.contrastText },
              '& .MuiListItemIcon-root': { minWidth: 40,},
              '& .MuiDivider-root': {
                borderColor: '#787A7E',
                backgroundColor: '#787A7E', // helps vertical/middle variants
              },
            },
          }}
        >
          <FilterDrawerContent filterElements={filterElements} />
        </Drawer>
      )}

      {/* PDF Report Modal */}
      <PdfReportModal
        open={isPdfReportModalOpen}
        onClose={handlePdfReportClose}
        autoLaunch={false}
      />
    </>
  );
};

export default AppBarComponent;